import { useCallback, useMemo, useRef } from 'react'
import { Tooltip } from 'antd'
import { CloseOutlined, SearchOutlined } from '@ant-design/icons'
import TitleAutocomplete from '../Table/TableComponents/TitleAutocomplete'
import { renderTranslation, stopPropagation } from '../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { formatString, setCapitalLetter } from '../../Helpers/StringHelpers'
import { TableColumnTranslationPrefix } from '../../Constants/Configuration'
import {
  ArticleColumn,
  initalHeaderMode,
  autocompleteHeaderMode,
  SubRuleColumns,
} from '../../Constants/TableConfiguration'
import {
  setActiveSubRuleAutocomplete,
  resetDynamicSubRuleLabelToInitial,
  setSubRuleAutocompleteValues,
} from '../../Slices/Omnichannel/SubRuleSlice'
import { populateSubRuleTableWithArticles } from '../../Services/Omnichannel/SeasonRuleService'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { removeHoverClassFromTag } from 'Helpers/FilterHelper'

function SubRuleDynamicLabel(props) {
  const dispatch = useAppDispatch()
  const activeAutocomplete = useAppSelector(state => state.present.subRule.activeAutocomplete)
  const language = useAppSelector(state => state.present.language.value)
  const tableFilters = useAppSelector(state => state.present.subRule.tableFilters)
  const emptyTableColumns = useAppSelector(state => state.present.subRule.emptyFilterColumns)

  const hasMultipleSearch = SubRuleColumns.find(x => x.name === props.label)?.multipleSearch

  const filter = useMemo(() => {
    return tableFilters[props.label]
  }, [props.label, tableFilters])

  const emptyColumnKeys = useMemo(() => {
    return Object.keys(emptyTableColumns)
  }, [emptyTableColumns])

  const isColumnEmpty = useMemo(() => {
    let isEmpty = false

    emptyColumnKeys.forEach(group => {
      if (emptyTableColumns[group].filter(element => element.value === props.label && element.active).length !== 0) {
        isEmpty = true
      }
    })

    return isEmpty
  }, [emptyColumnKeys, emptyTableColumns, props.label])

  const filterLabelToSet = useMemo(() => {
    return filter && isColumnEmpty
      ? formatString(
          renderTranslation(language, TranslationKeys.tableEmptyColumnsAutocomplete),
          renderTranslation(language, TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(props.label)]),
        )
      : filter
  }, [filter, isColumnEmpty, language, props.label])

  const headerMode = useMemo(() => {
    return !filter && activeAutocomplete !== props.label
      ? initalHeaderMode
      : activeAutocomplete === props.label
      ? autocompleteHeaderMode
      : ''
  }, [filter, props.label, activeAutocomplete])

  const resetMultipleSearchInput = useCallback(() => {
    if (hasMultipleSearch) {
      removeHoverClassFromTag(props.label)
      dispatch(setSubRuleAutocompleteValues({ label: props.label, text: '' }))
    }
  }, [dispatch, hasMultipleSearch, props.label])

  const switchToInitial = useCallback(
    e => {
      e.stopPropagation()
      const objectToDispatch = { label: props.label, columnEmpty: isColumnEmpty, languageCode: props.language }

      dispatch(resetDynamicSubRuleLabelToInitial(objectToDispatch))
      dispatch(setSubRuleAutocompleteValues({ label: props.label, text: '' }))
      inputElement.current.parentNode.parentNode.parentNode.removeAttribute('draggable')

      dispatch(populateSubRuleTableWithArticles(false))
    },
    [dispatch, isColumnEmpty, props.label, props.language],
  )

  const switchToAutocomplete = useCallback(
    e => {
      e.stopPropagation()
      if (props.disableSearch) return
      resetMultipleSearchInput()
      dispatch(setActiveSubRuleAutocomplete(props.label))
      inputElement.current.parentNode.parentNode.parentNode.draggable = false
    },
    [dispatch, props.disableSearch, props.label, resetMultipleSearchInput],
  )

  const reSwitchToAutocomplete = useCallback(
    e => {
      e.stopPropagation()
      if (props.disableSearch) return

      if (isColumnEmpty) return
      resetMultipleSearchInput()
      dispatch(setActiveSubRuleAutocomplete(props.label))
      inputElement.current.parentNode.parentNode.parentNode.draggable = false
    },
    [dispatch, isColumnEmpty, props.disableSearch, props.label, resetMultipleSearchInput],
  )

  const handleClick = useCallback(
    e => {
      if (activeAutocomplete !== '') {
        dispatch(setActiveSubRuleAutocomplete(''))
        inputElement.current?.parentNode?.parentNode?.parentNode?.removeAttribute('draggable')
      }
    },
    [activeAutocomplete, dispatch],
  )

  const setAutocompleteWindowListener = useCallback(() => {
    if (!filter) {
      if (headerMode === autocompleteHeaderMode) {
        window.addEventListener('click', handleClick)
      } else {
        window.removeEventListener('click', handleClick)
      }
    }
    return () => {
      window.removeEventListener('click', handleClick)
    }
  }, [filter, handleClick, headerMode])

  setAutocompleteWindowListener()

  const onFocusChange = useCallback(() => {
    dispatch(setActiveSubRuleAutocomplete(props.label))
  }, [dispatch, props.label])

  const removeAutocomplete = () => {
    dispatch(setActiveSubRuleAutocomplete(''))
    inputElement.current.parentNode.parentNode.parentNode.removeAttribute('draggable')
  }

  const inputElement = useRef(null)

  const renderTitleAutoComplete = () => {
    return (
      <TitleAutocomplete
        style={props.style}
        title={renderTranslation(
          language,
          TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(props.label)],
        )}
        label={props.label}
        onSelect={removeAutocomplete}
        onClick={stopPropagation}
        isFromSubRule={true}
      />
    )
  }

  return (
    <>
      <div ref={inputElement}></div>
      {filter ? (
        headerMode !== autocompleteHeaderMode ? (
          <Tooltip title={filter && isColumnEmpty ? filterLabelToSet : undefined}>
            <div
              style={props.style}
              className={
                'complete-search-label truncate-text' + (props.label === ArticleColumn.Size ? ' size-search-label' : '')
              }
            >
              <span className='clear-search-icon' onClick={switchToInitial}>
                <CloseOutlined className='general-clear-icon cursor-pointer' />
              </span>
              <span
                className={isColumnEmpty ? 'search-empty cursor-pointer' : 'cursor-pointer'}
                onClick={reSwitchToAutocomplete}
                tabIndex={0}
                onMouseLeave={() => {
                  removeHoverClassFromTag(props.label)
                }}
              >
                {hasMultipleSearch && filter.split('|').length > 1 && !isColumnEmpty
                  ? filter.split('|').length + renderTranslation(language, TranslationKeys.multipleSearchResultsLabel)
                  : filterLabelToSet}
                {hasMultipleSearch && !isColumnEmpty ? <span className='ml-1'>+</span> : null}
              </span>
            </div>
          </Tooltip>
        ) : (
          renderTitleAutoComplete()
        )
      ) : headerMode === autocompleteHeaderMode ? (
        renderTitleAutoComplete()
      ) : (
        <div
          style={props.style}
          className={'custom-article-title' + (props.label === ArticleColumn.Size ? ' size-title' : '')}
          onClick={switchToAutocomplete}
        >
          {renderTranslation(language, TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(props.label)])}{' '}
          {props.language}{' '}
          {!props.disableSearch && (
            <SearchOutlined
              style={props.label === ArticleColumn.Size ? { paddingLeft: 5 } : { paddingLeft: 8 }}
              tabIndex={0}
              onFocus={onFocusChange}
            />
          )}
        </div>
      )}
    </>
  )
}

export default SubRuleDynamicLabel
